import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Card, CardBody, Col, Container, Row, Table, Button } from "reactstrap";
import Breadcrumb from "../../../layout/breadcrumb";
import withReducer from "../../../store/withReducer";
import orderReducer from "../store";
import { getTrackingById, downloadTrackingLabel, downloadTrackingLabelQRCode, clearTrackingDetails } from "../store/trackingSlice";
import { formatterCurrency } from "../../../utils/common";
import PreLoader from "../../../share-components/ui/preLoader";
import ReturnProcessTimeline from "../pending-return/view/content/ReturnProcessTimeline";
import "../pending-return/styles.scss";

const ViewTracking = () => {
    const { id } = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const trackingState = useSelector(({ order }) => order.tracking);
    
    // Use data from route state if available, otherwise try to get from Redux or API
    const routeTrackingData = location.state?.trackingData;

    const dataMenu = [
        {
            type: 1,
            text: "Trackings",
            link: "/order/trackings",
        },
        {
            type: 0,
            text: "View Tracking",
        },
    ];

    useEffect(() => {
        // Always fetch full tracking details by id when we have id, so we get tracking_summary (timeline events)
        if (id) {
            setLoading(true);
            dispatch(getTrackingById(id))
                .then((res) => {
                    setLoading(false);
                    if (res.error) {
                        history.push("/order/trackings");
                    }
                })
                .catch(() => {
                    setLoading(false);
                    history.push("/order/trackings");
                });
        } else {
            setLoading(false);
        }

        return () => {
            dispatch(clearTrackingDetails());
        };
    }, [dispatch, id, history]);

    // Shared download handler utility
    const downloadFile = (url) => {
        if (!url) return;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleDownloadLabel = () => {
        if (!finalTrackingDetails?.id) return;
        dispatch(downloadTrackingLabel({ id: finalTrackingDetails.id })).then((res) => {
            if (res.payload) downloadFile(res.payload);
        });
    };

    const handleDownloadLabelQRCode = () => {
        if (!finalTrackingDetails?.id) return;
        dispatch(downloadTrackingLabelQRCode({ id: finalTrackingDetails.id })).then((res) => {
            if (res.payload) downloadFile(res.payload);
        });
    };

    const getStatusBadgeClass = (status) => {
        if (status === "Delivered") return "success";
        if (status === "In Transit") return "warning";
        return "secondary";
    };

    const getItemStatusBadgeClass = (status) => {
        if (status === "Accepted") return "success";
        if (status === "Pending") return "warning";
        return "secondary";
    };

    // Prefer API details (includes tracking_summary for timeline); fallback to route state or list
    const apiDetails = trackingState.trackingDetails && parseInt(trackingState.trackingDetails.id) === parseInt(id)
        ? trackingState.trackingDetails
        : null;
    const finalTrackingDetails = apiDetails ||
        routeTrackingData ||
        trackingState.trackingsList?.find(t => t.id === parseInt(id));

    if (loading && !finalTrackingDetails) {
        return <PreLoader />;
    }

    if (!finalTrackingDetails) {
        return (
            <Fragment>
                <Breadcrumb title="View Tracking" data={dataMenu} />
                <Container fluid={true}>
                    <Card>
                        <CardBody>
                            <p>Tracking not found.</p>
                            <Button color="primary" onClick={() => history.push("/order/trackings")}>
                                Back to Trackings
                            </Button>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    const items = finalTrackingDetails.items || [];
    const canLabel = finalTrackingDetails.actions?.is_available_label;
    const canQr = finalTrackingDetails.actions?.is_available_label_qrcode;
    const isOrderType = finalTrackingDetails.type === "Order";

    // Get seller name for ribbon (use sellerLists if available, otherwise seller, fallback to order name)
    const sellerName = finalTrackingDetails.sellerLists && finalTrackingDetails.sellerLists.length > 0
        ? finalTrackingDetails.sellerLists[0]
        : finalTrackingDetails.seller || finalTrackingDetails.orderName || "Unknown";

    // Check if we have tracking information
    const hasTracking = finalTrackingDetails.trackingNumber || finalTrackingDetails.trackingUrl;
    const trackingSummary = finalTrackingDetails.tracking_summary;

    return (
        <Fragment>
            <Breadcrumb title="View Tracking" data={dataMenu} />
            <Container fluid={true}>
                <Row>
                    <Col md="12">
                        <Card className="ribbon-wrapper mb-3">
                            <CardBody>
                                <div className="ribbon ribbon-clip h6 ribbon-info">
                                    {sellerName}
                                </div>

                                <div className="table-responsive">
                                    <Table style={{ width: '100%', border: "none" }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: '50%', paddingLeft: 0 }}>
                                                    {hasTracking ? (
                                                        <>
                                                            <p><strong>Tracking Details:</strong></p>
                                                            <p>
                                                                <strong>Status:</strong>{" "}
                                                                <span className={`badge badge-${getStatusBadgeClass(finalTrackingDetails.trackingStatus)}`}>
                                                                    {finalTrackingDetails.trackingStatus || "Pending"}
                                                                </span>
                                                            </p>
                                                            <p><strong>Carrier:</strong> {finalTrackingDetails.deliveryPartner || "-"}</p>
                                                            <p><strong>Tracking Number:</strong> {finalTrackingDetails.trackingNumber || "-"}</p>
                                                            <p>
                                                                <strong>Tracking URL:</strong>{" "}
                                                                {finalTrackingDetails.trackingUrl ? (
                                                                    <a
                                                                        style={{ textDecoration: 'underline', color: 'blue' }}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        href={finalTrackingDetails.trackingUrl}
                                                                    >
                                                                        {finalTrackingDetails.trackingUrl}
                                                                    </a>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p><strong>Tracking Details:</strong> Not available</p>
                                                            <p>
                                                                <strong>Status:</strong>{" "}
                                                                <span className={`badge badge-${getStatusBadgeClass(finalTrackingDetails.trackingStatus)}`}>
                                                                    {finalTrackingDetails.trackingStatus || "Pending"}
                                                                </span>
                                                            </p>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {!isOrderType && (
                                                        <>
                                                            <p>
                                                                <strong style={{ width: '100px' }}>Return Label:</strong>{" "}
                                                                <Button
                                                                    size="xs"
                                                                    color="info"
                                                                    className="pt-1"
                                                                    disabled={!canLabel}
                                                                    onClick={handleDownloadLabel}
                                                                >
                                                                    Download Return Label
                                                                </Button>
                                                            </p>
                                                            {canQr && (
                                                                <p>
                                                                    <strong style={{ width: '100px' }}>QR Code:</strong>{" "}
                                                                    <Button
                                                                        size="xs"
                                                                        color="success"
                                                                        className="pt-1"
                                                                        disabled={!canQr}
                                                                        onClick={handleDownloadLabelQRCode}
                                                                    >
                                                                        Download QR Code
                                                                    </Button>
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                {trackingSummary && trackingSummary.events && trackingSummary.events.length > 0 && (
                                    <div className="mt-2 mb-5">
                                        <h6>Tracking Status:</h6>
                                        <ReturnProcessTimeline trackingSummary={trackingSummary} />
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {items.length > 0 && (
                            <Card className="ribbon-wrapper">
                                <CardBody>
                                    <div className="ribbon ribbon-clip h6 ribbon-dark">
                                        Items ({items.length})
                                    </div>
                                    <div className="table-responsive mt-3">
                                        <Table style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th>Qty</th>
                                                    <th>Customer Reason</th>
                                                    <th>Status</th>
                                                    <th>Price</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {item.image_path && (
                                                                    <img
                                                                        src={item.image_path}
                                                                        alt={item.image_alt || item.product_name || "Product"}
                                                                        className="mr-2"
                                                                        style={{
                                                                            width: "80px",
                                                                            height: "80px",
                                                                            objectFit: "contain",
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                        }}
                                                                    />
                                                                )}
                                                                <div>
                                                                    <div className="font-weight-600">{item.product_name || "-"}</div>
                                                                    <div className="text-muted small">{item.sku || "-"}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{item.quantity ?? "-"}</td>
                                                        <td>{item.reason || "-"}</td>
                                                        <td>
                                                            <span className={`badge badge-${getItemStatusBadgeClass(item.status)}`}>
                                                                {item.status || "-"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {item.price != null
                                                                ? formatterCurrency(parseFloat(item.price), "GBP")
                                                                : "-"}
                                                        </td>
                                                        <td>
                                                            {item.total != null
                                                                ? formatterCurrency(item.total, "GBP")
                                                                : "-"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ order: orderReducer }])(ViewTracking);
